/* Nova Community Gallery v13 — Instagram-style UI (V1 local persistence) */
(() => {
  'use strict';

  // Singleton guard (avoid double-init in theme editor / section reloads)
  if (window.NovaCommunityGalleryV13?.initialized) return;

  const state = {
    initialized: true,
    posts: [],
    activePost: null,
    liked: new Set(),
    comments: new Map(), // postId -> array
  };

  const LS_KEY_LIKES = 'nova_gallery_v13_likes';
  const LS_KEY_COMMENTS = 'nova_gallery_v13_comments';

  function loadLocal() {
    try {
      const likedArr = JSON.parse(localStorage.getItem(LS_KEY_LIKES) || '[]');
      state.liked = new Set(likedArr);
    } catch { state.liked = new Set(); }

    try {
      const raw = JSON.parse(localStorage.getItem(LS_KEY_COMMENTS) || '{}');
      state.comments = new Map(Object.entries(raw).map(([k, v]) => [k, Array.isArray(v) ? v : []]));
    } catch { state.comments = new Map(); }
  }

  function saveLikes() {
    try { localStorage.setItem(LS_KEY_LIKES, JSON.stringify(Array.from(state.liked))); } catch {}
  }

  function saveComments() {
    try {
      const obj = {};
      for (const [k, v] of state.comments.entries()) obj[k] = v;
      localStorage.setItem(LS_KEY_COMMENTS, JSON.stringify(obj));
    } catch {}
  }

  function q(sel, root=document){ return root.querySelector(sel); }
  function qa(sel, root=document){ return Array.from(root.querySelectorAll(sel)); }

  function init() {
    loadLocal();

    const root = q('[data-ng-root]');
    if (!root) return;

    const grid = q('[data-ng-grid]', root);
    const modal = q('[data-ng-modal]', root);
    const closeBtn = q('[data-ng-close]', root);
    const backdrop = q('[data-ng-backdrop]', root);

    // Collect posts from DOM
    state.posts = qa('[data-ng-post-id]', root).map(el => {
      return {
        id: el.getAttribute('data-ng-post-id') || '',
        src: el.getAttribute('data-ng-src') || '',
        alt: el.getAttribute('data-ng-alt') || 'Gallery post',
        caption: el.getAttribute('data-ng-caption') || '',
        user: el.getAttribute('data-ng-user') || 'Nova Collector',
        time: el.getAttribute('data-ng-time') || 'Recently',
        likes: parseInt(el.getAttribute('data-ng-likes') || '0', 10),
        viewer: el.getAttribute('data-ng-viewer') || 'Guest'
      };
    }).filter(p => p.id);

    // Apply liked state to tiles
    for (const p of state.posts) {
      const tile = q(`[data-ng-post-id="${cssEscape(p.id)}"]`, root);
      if (!tile) continue;
      tile.setAttribute('data-liked', state.liked.has(p.id) ? '1' : '0');
    }

    // Tile click => open modal
    grid?.addEventListener('click', (e) => {
      const tile = e.target.closest('[data-ng-post-id]');
      if (!tile) return;
      const id = tile.getAttribute('data-ng-post-id');
      const post = state.posts.find(x => x.id === id);
      if (!post) return;
      openPost(post, root);
    });

    // Close
    closeBtn?.addEventListener('click', () => closeModal(root));
    backdrop?.addEventListener('click', () => closeModal(root));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal(root);
      if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && modal?.getAttribute('aria-hidden') === 'false') {
        const idx = state.posts.findIndex(p => p.id === state.activePost?.id);
        if (idx >= 0) {
          const next = e.key === 'ArrowRight' ? state.posts[idx + 1] : state.posts[idx - 1];
          if (next) openPost(next, root, true);
        }
      }
    });

    // Deep-link: ?post=ID
    const url = new URL(window.location.href);
    const postId = url.searchParams.get('post');
    if (postId) {
      const post = state.posts.find(x => x.id === postId);
      if (post) openPost(post, root, true);
    }
  }

  function openPost(post, root, silentUrlUpdate=false) {
    state.activePost = post;

    const modal = q('[data-ng-modal]', root);
    const img = q('[data-ng-modal-img]', root);
    const name = q('[data-ng-modal-name]', root);
    const time = q('[data-ng-modal-time]', root);
    const caption = q('[data-ng-modal-caption]', root);
    const likesLine = q('[data-ng-modal-likes]', root);
    const likeBtn = q('[data-ng-like]', root);
    const shareBtn = q('[data-ng-share]', root);
    const commentBtn = q('[data-ng-comment]', root);
    const commentsWrap = q('[data-ng-comments]', root);
    const input = q('[data-ng-input]', root);
    const send = q('[data-ng-send]', root);

    if (img) { img.src = post.src; img.alt = post.alt || 'Gallery post'; }
    if (name) name.textContent = post.user || 'Nova Collector';
    if (time) time.textContent = post.time || '';
    if (caption) caption.textContent = post.caption || '';
    if (likesLine) likesLine.textContent = formatLikes(getLikes(post));

    const isLiked = state.liked.has(post.id);
    if (likeBtn) likeBtn.classList.toggle('liked', isLiked);

    renderComments(post.id, commentsWrap);

    // Wire actions
    likeBtn?.addEventListener('click', onLike, { once: true });
    shareBtn?.addEventListener('click', onShare, { once: true });
    commentBtn?.addEventListener('click', () => input?.focus(), { once: true });
    send?.addEventListener('click', onSend, { once: true });
    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') onSend();
    }, { once: true });

    modal?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (!silentUrlUpdate) {
      const url = new URL(window.location.href);
      url.searchParams.set('post', post.id);
      window.history.replaceState({}, '', url.toString());
    }

    function onLike() {
      const liked = state.liked.has(post.id);
      if (liked) state.liked.delete(post.id);
      else state.liked.add(post.id);
      saveLikes();

      const nowLiked = state.liked.has(post.id);
      likeBtn?.classList.toggle('liked', nowLiked);

      // Update tile metric & modal line
      const tileLikes = q(`[data-ng-like-count="${cssEscape(post.id)}"]`, root);
      const count = nowLiked ? (post.likes + 1) : post.likes;
      if (tileLikes) tileLikes.textContent = count.toLocaleString();
      if (likesLine) likesLine.textContent = formatLikes(count);
    }

    async function onShare() {
      const shareUrl = new URL(window.location.href);
      shareUrl.searchParams.set('post', post.id);

      const payload = { title: "Nova's Gallery", text: post.caption || "Nova's Gems", url: shareUrl.toString() };

      try {
        if (navigator.share) {
          await navigator.share(payload);
          return;
        }
      } catch {}

      try {
        await navigator.clipboard.writeText(shareUrl.toString());
        flashToast(root, 'Link copied');
      } catch {
        flashToast(root, 'Copy failed');
      }
    }

    function onSend() {
      const val = (input?.value || '').trim();
      if (!val) return;

      const list = state.comments.get(post.id) || [];
      const who = post.viewer || 'Guest';
      list.unshift({ who, txt: val, ts: Date.now() });
      state.comments.set(post.id, list);
      saveComments();

      if (input) input.value = '';
      renderComments(post.id, commentsWrap);
    }
  }

  function closeModal(root) {
    const modal = q('[data-ng-modal]', root);
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;

    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // remove ?post=
    const url = new URL(window.location.href);
    url.searchParams.delete('post');
    window.history.replaceState({}, '', url.toString());

    state.activePost = null;
  }

  function renderComments(postId, wrap) {
    if (!wrap) return;
    const list = state.comments.get(postId) || [];
    wrap.innerHTML = '';

    if (list.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'ng-caption';
      empty.textContent = 'No comments yet.';
      wrap.appendChild(empty);
      return;
    }

    for (const c of list.slice(0, 50)) {
      const row = document.createElement('div');
      row.className = 'ng-comment';
      row.innerHTML = `<div class="who"></div><div class="txt"></div>`;
      q('.who', row).textContent = c.who || 'Guest';
      q('.txt', row).textContent = c.txt || '';
      wrap.appendChild(row);
    }
  }

  function getLikes(post) {
    return state.liked.has(post.id) ? (post.likes + 1) : post.likes;
  }

  function formatLikes(n) {
    return `${n.toLocaleString()} likes`;
  }

  function flashToast(root, text) {
    let toast = q('[data-ng-toast]', root);
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute('data-ng-toast','');
      toast.style.position = 'fixed';
      toast.style.left = '50%';
      toast.style.bottom = '22px';
      toast.style.transform = 'translateX(-50%)';
      toast.style.padding = '10px 14px';
      toast.style.borderRadius = '999px';
      toast.style.background = 'rgba(2,15,42,.86)';
      toast.style.color = '#fff';
      toast.style.fontFamily = 'Inter, sans-serif';
      toast.style.fontSize = '13px';
      toast.style.fontWeight = '800';
      toast.style.zIndex = '11000';
      toast.style.border = '1px solid rgba(255,255,255,.18)';
      toast.style.backdropFilter = 'blur(10px)';
      document.body.appendChild(toast);
    }
    toast.textContent = text;
    toast.style.opacity = '1';
    clearTimeout(toast._t);
    toast._t = setTimeout(()=> toast.style.opacity = '0', 1400);
  }

  // CSS.escape fallback
  function cssEscape(str){
    if (window.CSS && CSS.escape) return CSS.escape(str);
    return String(str).replace(/["\\]/g, '\\$&');
  }

  window.NovaCommunityGalleryV13 = state;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();